import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { flattenToAppURL } from '@plone/volto/helpers';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { useIntl } from 'react-intl';
import Component from '@plone/volto/components/theme/Component/Component';
import {
  getEventCard,
  getEventDate,
  getEventTime,
} from 'volto-advanced-variations/sharedUtils';
import processItemsForRecurrence from 'volto-advanced-variations/processItemsForRecurrence';
import RenderImage from 'volto-advanced-variations/renderImage';

const columnClassMap = {
  1: 'one',
  2: 'two',
  3: 'three',
  4: 'four',
  5: 'five',
  6: 'six',
  7: 'seven',
  8: 'eight',
  9: 'nine',
  10: 'ten',
  11: 'eleven',
  12: 'twelve',
};

const CommonItemRenderer = ({
  items,
  showRecurrence,
  quote,
  showTitle,
  eventCard,
  titleTag: TitleTag = 'h3',
  eventDate,
  eventTime,
  eventLocation,
  showDescription,
  effectiveDate,
  expirationDate,
  isEditMode,
  imageSide,
  imageWidth = 4,
  howManyColumns,
  fetchPriority,
  creatorauthor,
  showTags,
}) => {
  const intl = useIntl();

  // Helper to build correct image URL based on type
  const buildImageUrl = (imageSrc, scale) => {
    if (!imageSrc) return null;
    
    if (imageSrc.type === 'linked' || imageSrc.type === 'embedded') {
      // Linked and embedded images need /@@images/image/ appended
      return `${imageSrc.basePath}/@@images/image/${scale}`;
    } else if (imageSrc.type === 'traditional') {
      // Traditional already has /@@images/image in basePath, just append scale
      return `${imageSrc.basePath}/${scale}`;
    }
    return null;
  };

  // Helper function to extract image source from various formats
  // Returns: null, or { basePath, type: 'linked' | 'embedded' | 'traditional' }
  const getImageSrc = (item) => {
    // NEW: image_scales is an object with preview_image_link array
    if (item.image_scales && typeof item.image_scales === 'object') {
      // Check if it has preview_image_link property with array (linked preview image)
      if (item.image_scales.preview_image_link && Array.isArray(item.image_scales.preview_image_link)) {
        const links = item.image_scales.preview_image_link;
        if (links.length > 0 && links[0]?.base_path) {
          console.log('Found preview_image_link with base_path:', links[0].base_path);
          return { basePath: links[0].base_path, type: 'linked' };
        }
      }

      // Check if it has image property with array (embedded image)
      if (item.image_scales.image && Array.isArray(item.image_scales.image)) {
        const images = item.image_scales.image;
        if (images.length > 0 && images[0]?.base_path) {
          console.log('Found image with base_path:', images[0].base_path);
          return { basePath: images[0].base_path, type: 'embedded' };
        }
      }
    }

    // Pattern: Traditional image_field (embedded image)
    if (item.image_field && item.image_field !== 'preview_image_link') {
      const src = `${flattenToAppURL(item['@id'])}/@@images/image`;
      console.log('Found traditional image_field:', src);
      return { basePath: src, type: 'traditional' };
    }

    return null;
  };

  // Process items based on recurrence setting
  const processedItems = useMemo(() => {
    if (showRecurrence) {
      return processItemsForRecurrence(items); // Sort by next recurrence date
    }
    return items.map((item) => ({
      ...item,
      url: flattenToAppURL(item['@id']),
      imageSrc: getImageSrc(item),
    }));
  }, [items, showRecurrence]);

  const renderMetadata = useMemo(() => {
    return (item) => (
      <>
        {eventDate && <span className="start-date">{getEventDate(item)}</span>}
        {eventTime && eventDate && <span> | </span>}
        {eventTime && (
          <span className="start-time">
            {getEventTime(item) === '12:00 AM - 11:59 PM'
              ? 'All Day'
              : getEventTime(item)}
          </span>
        )}
      </>
    );
  }, [eventDate, eventTime]);

  const renderContent = (item) => (
    <>
      {quote && (
        <>
          <blockquote>{item.description}</blockquote>
          <div className="styled-slate right has--align--right align styled">
            {item.title || item.id}
          </div>
        </>
      )}

      {eventCard && getEventCard(item)}
      {(eventDate || eventTime) && (
        <div className="advancedDatetime">{renderMetadata(item)}</div>
      )}
      {showTags && item.Subject && item.Subject.length > 0 && (
        <div className="item-tags">
          {item.Subject.map((tag, index) => (
            <span key={index} className="tag">
              {tag}
            </span>
          ))}
        </div>
      )}
      {showTitle && (
        <TitleTag className="threelines">
          {imageSide === 'background' ? (
            item.title || item.id
          ) : !isEditMode ? (
            <a href={item.url} target="_blank" rel="noopener noreferrer">
              {item.title ? item.title : item.id}
            </a>
          ) : (
            item.title || item.id
          )}
        </TitleTag>
      )}

      {creatorauthor && <p className="author">{item.Creator}</p>}
      {eventLocation && <p className="location">{item.location}</p>}
      {effectiveDate && (
        <p className="effectiveDate">
          {moment(item.effective).format('DD MMMM, YYYY')}
        </p>
      )}
      {expirationDate && <p>Expiration: {moment(item.expires).format('L')}</p>}
      {showDescription && item.description && <p>{item.description}</p>}
    </>
  );

  return (
    <div className="fade-in loaded">
      {processedItems.map((item) => {
        if (item.imageSrc) {
          console.log('=== RENDER TIME imageSrc ===');
          console.log('item.imageSrc:', item.imageSrc);
          console.log('Expected srcSet:', `${buildImageUrl(item.imageSrc, 'preview')}`);
        }
        return (
          <div key={item['@id']} className="ui one column grid advanced-item">
            <div className={`column ${imageSide}`}>
            {imageSide === 'background' ? (
              <div className="backgroundimage">
                <a href={item.url} condition={!isEditMode} target="_blank" rel="noopener noreferrer">
                  <div className="focuspoint">
                    {item.imageSrc ? (
                      <a href={item.url} target="_blank" rel="noopener noreferrer">
                        <img
                          sizes="(min-width: 768px) and (max-width: 991px) 171px, (min-width: 992px) and (max-width: 1199px) 223px, (min-width: 1200px) 272px"
                          srcSet={`
                            ${buildImageUrl(item.imageSrc, 'mini')} 200w,
                            ${buildImageUrl(item.imageSrc, 'preview')} 400w,
                            ${buildImageUrl(item.imageSrc, 'teaser')} 600w,
                            ${buildImageUrl(item.imageSrc, 'large')} 800w,
                            ${buildImageUrl(item.imageSrc, 'larger')} 1000w,
                            ${buildImageUrl(item.imageSrc, 'great')} 1200w,
                            ${buildImageUrl(item.imageSrc, 'huge')} 1600w
                          `}
                          src={`${buildImageUrl(item.imageSrc, 'preview')}`}
                          alt={item.title || ''}
                          className="ui image listImage"
                          loading={fetchPriority === 'high' ? 'eager' : 'lazy'}
                          fetchpriority={fetchPriority}
                          width="100%"
                          height="auto"
                          style={{ aspectRatio: '16/9' }}
                        />
                      </a>
                    ) : (
                      <Component
                        componentName="PreviewImage"
                        item={item}
                        alt={item.title || ''}
                      />
                    )}
                  </div>
                  <div className="info-text">{renderContent(item)}</div>
                </a>
              </div>
            ) : (
              <div className="ui grid">
                {['up', 'left'].includes(imageSide) && (
                  <div
                    className={`${
                      ['up', 'down'].includes(imageSide)
                        ? 'twelve'
                        : columnClassMap[imageWidth]
                    } wide column advancedImage`}
                  >
                    <a href={item.url} target="_blank" rel="noopener noreferrer">
                      {item.imageSrc ? (
                        <img
                          sizes="(min-width: 768px) and (max-width: 991px) 171px, (min-width: 992px) and (max-width: 1199px) 223px, (min-width: 1200px) 272px"
                          srcSet={`
                            ${buildImageUrl(item.imageSrc, 'mini')} 200w,
                            ${buildImageUrl(item.imageSrc, 'preview')} 400w,
                            ${buildImageUrl(item.imageSrc, 'teaser')} 600w,
                            ${buildImageUrl(item.imageSrc, 'large')} 800w,
                            ${buildImageUrl(item.imageSrc, 'larger')} 1000w,
                            ${buildImageUrl(item.imageSrc, 'great')} 1200w,
                            ${buildImageUrl(item.imageSrc, 'huge')} 1600w
                          `}
                          src={`${buildImageUrl(item.imageSrc, 'preview')}`}
                          alt={item.title || ''}
                          className="ui image listImage"
                          loading={fetchPriority === 'high' ? 'eager' : 'lazy'}
                          fetchpriority={fetchPriority}
                          width="100%"
                          height="auto"
                          style={{ aspectRatio: '16/9' }}
                        />
                      ) : (
                        <Component
                          componentName="PreviewImage"
                          item={item}
                          alt={item.title || ''}
                        />
                      )}
                    </a>
                  </div>
                )}
                <div
                  className={`${
                    ['up', 'down'].includes(imageSide) ||
                    !['left', 'right'].includes(imageSide)
                      ? 'twelve'
                      : columnClassMap[12 - imageWidth]
                  } wide column`}
                >
                  {renderContent(item)}
                </div>
                {['right', 'down'].includes(imageSide) && (
                  <div
                    className={`${
                      ['up', 'down'].includes(imageSide)
                        ? 'twelve'
                        : columnClassMap[imageWidth]
                    } wide column advancedImage`}
                  >
                    <a href={item.url} target="_blank" rel="noopener noreferrer">
                      {item.imageSrc ? (
                        <img
                          sizes="(min-width: 768px) and (max-width: 991px) 171px, (min-width: 992px) and (max-width: 1199px) 223px, (min-width: 1200px) 272px"
                          srcSet={`
                            ${buildImageUrl(item.imageSrc, 'mini')} 200w,
                            ${buildImageUrl(item.imageSrc, 'preview')} 400w,
                            ${buildImageUrl(item.imageSrc, 'teaser')} 600w,
                            ${buildImageUrl(item.imageSrc, 'large')} 800w,
                            ${buildImageUrl(item.imageSrc, 'larger')} 1000w,
                            ${buildImageUrl(item.imageSrc, 'great')} 1200w,
                            ${buildImageUrl(item.imageSrc, 'huge')} 1600w
                          `}
                          src={`${buildImageUrl(item.imageSrc, 'preview')}`}
                          alt={item.title || ''}
                          className="ui image listImage"
                          loading={fetchPriority === 'high' ? 'eager' : 'lazy'}
                          fetchpriority={fetchPriority}
                          width="100%"
                          height="auto"
                          style={{ aspectRatio: '16/9' }}
                        />
                      ) : (
                        <Component
                          componentName="PreviewImage"
                          item={item}
                          alt={item.title || ''}
                        />
                      )}
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        );
      })}
    </div>
  );
};

CommonItemRenderer.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  showRecurrence: PropTypes.bool,
  quote: PropTypes.bool,
  showTitle: PropTypes.bool,
  eventCard: PropTypes.bool,
  titleTag: PropTypes.string,
  eventDate: PropTypes.bool,
  eventTime: PropTypes.bool,
  eventLocation: PropTypes.bool,
  showDescription: PropTypes.bool,
  effectiveDate: PropTypes.bool,
  expirationDate: PropTypes.bool,
  isEditMode: PropTypes.bool,
  imageSide: PropTypes.string,
  imageWidth: PropTypes.number,
  howManyColumns: PropTypes.number,
  fetchPriority: PropTypes.string,
  creatorauthor: PropTypes.bool,
  showTags: PropTypes.bool,
};

export default CommonItemRenderer;
